import * as React from 'react'

import * as SheetPrimitive from '@radix-ui/react-dialog'

import { Icon } from '@/components/icon'
import { cn } from '@/infra'

const SheetCloseClassnameContext = React.createContext<string | undefined>(undefined)

interface SheetCloseButtonProps {
  className?: string
}

function SheetCloseButton({ className }: Readonly<SheetCloseButtonProps>) {
  return (
    <SheetPrimitive.Close
      className={cn(
        'ring-offset-background focus:ring-ring data-[state=open]:bg-secondary cursor-pointer rounded-xs transition-opacity hover:opacity-70 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none',
        className,
      )}
    >
      <Icon name="close" className="text-primary size-8" />
      <span className="sr-only">Fechar</span>
    </SheetPrimitive.Close>
  )
}

function Sheet({ ...props }: Readonly<React.ComponentProps<typeof SheetPrimitive.Root>>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({ ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({ ...props }: Readonly<React.ComponentProps<typeof SheetPrimitive.Portal>>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80',
        className,
      )}
      {...props}
    />
  )
}

function SheetContent({
  className,
  children,
  side = 'right',
  preventDismiss = false,
  closeClassname,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: 'top' | 'right' | 'bottom' | 'left'
  preventDismiss?: boolean
  closeClassname?: string
}) {
  return (
    <SheetPortal>
      <SheetOverlay />

      <SheetPrimitive.Content
        onInteractOutside={preventDismiss ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={preventDismiss ? (e) => e.preventDefault() : undefined}
        data-slot="sheet-content"
        className={cn(
          'bg-foreground data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 p-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 sm:gap-6 sm:p-6',
          side === 'right' &&
            'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm',
          side === 'left' &&
            'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm',
          side === 'top' &&
            'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b',
          side === 'bottom' &&
            'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t',
          className,
        )}
        {...props}
      >
        <SheetCloseClassnameContext.Provider value={closeClassname}>{children}</SheetCloseClassnameContext.Provider>
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({
  className,
  children,
  leftIcon,
  hideClose = false,
  ...props
}: React.ComponentProps<'div'> & {
  leftIcon?: React.ReactNode
  hideClose?: boolean
}) {
  const closeClassname = React.useContext(SheetCloseClassnameContext)
  const hasTopBar = !!leftIcon || !hideClose

  return (
    <div data-slot="sheet-header" className={cn('flex flex-col gap-4 sm:gap-6', className)} {...props}>
      {hasTopBar && (
        <div className={cn('flex items-center', leftIcon ? 'justify-between' : 'justify-end')}>
          {leftIcon ? <div className="shrink-0">{leftIcon}</div> : null}
          {hideClose ? null : <SheetCloseButton className={closeClassname} />}
        </div>
      )}
      {children ? <div className="flex flex-col gap-1.5">{children}</div> : null}
    </div>
  )
}

const sheetFooterVariants = {
  sticky:
    'fixed right-0 bottom-0 left-0 bg-white shadow-[0_-2px_10px_0_rgba(0,0,0,0.1)] sm:relative sm:grid sm:shadow-none',
} as const

function SheetFooter({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & {
  variant?: keyof typeof sheetFooterVariants
}) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn(
        'mt-auto flex flex-col gap-2 p-0 px-6 pt-4 pb-6 sm:p-0',
        variant && sheetFooterVariants[variant],
        className,
      )}
      {...props}
    />
  )
}

const sheetTitleVariants = {
  action: 'text-base sm:text-xl sm:font-poppins font-normal uppercase',
  info: 'text-sm font-nunito font-semibold',
} as const

function SheetTitle({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title> & {
  variant?: keyof typeof sheetTitleVariants
}) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('tracking-tight text-neutral-700', variant && sheetTitleVariants[variant], className)}
      {...props}
    />
  )
}

function SheetDescription({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

export { Sheet, SheetTrigger, SheetClose, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription }
